import { memo, useMemo } from 'react'

import {
  Badge,
  Box,
  Center,
  HStack,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react'
import {
  IAgoraRTCRemoteUser,
  IRemoteVideoTrack,
  RemoteVideoTrack,
} from 'agora-rtc-react'
import { CameraOff, MicOff, MicOn } from 'src/components/icon'

import { SpeakerIndicator } from '../SpeakerIndicator/SpeakerIndicator'

type Props = {
  remoteUser: IAgoraRTCRemoteUser
  userName: string | undefined
  isSmall?: boolean
}

export const VideoCallRoomRemotePlayer: React.FC<Props> = ({
  remoteUser,
  userName,
  isSmall = false,
}) => {
  const fontSize = useBreakpointValue(
    { base: 'sm', lg: 'md' },
    { fallback: 'sm' },
  )
  const micIconSize = useBreakpointValue(
    { base: '12px', lg: '14px' },
    { fallback: '12px' },
  )
  const badgeMaxWidth = useBreakpointValue(
    { base: '35%', md: '50%' },
    { fallback: '35%' },
  )

  const videoTrack = useMemo(() => {
    return remoteUser.videoTrack
  }, [remoteUser.videoTrack])

  return (
    <Box w="full" h="full" borderRadius="sm" pos="relative" bg="gray.600">
      <SpeakerIndicator
        audioTrack={remoteUser.audioTrack}
        enabled={!!remoteUser.audioTrack}
      />
      {videoTrack ? (
        <RemoteVideoPlayer track={videoTrack} />
      ) : (
        <Center flexDirection="column" color="white" w="full" h="full">
          <CameraOff size={isSmall ? '32px' : '96px'} />
        </Center>
      )}
      <Badge
        pos="absolute"
        left="8px"
        bottom="8px"
        px="2"
        bg="gray.500"
        color="white"
        fontWeight="normal"
        fontSize={isSmall ? 'sm' : fontSize}
        maxW={isSmall ? 'calc(100% - 1rem)' : badgeMaxWidth}
      >
        <HStack align="center">
          {!!userName && (
            // Note: Badgeの内部におけるtextは自動的でUpper caseになるのでtextTransformを明示する。
            <Text
              flex="1"
              textTransform="none"
              wordBreak="keep-all"
              whiteSpace="nowrap"
              overflow="hidden"
              textOverflow="ellipsis"
            >
              {userName}
            </Text>
          )}
          <Box w={micIconSize}>
            {remoteUser.audioTrack ? (
              <MicOn size={isSmall ? '12px' : micIconSize} />
            ) : (
              <MicOff size={isSmall ? '12px' : micIconSize} />
            )}
          </Box>
        </HStack>
      </Badge>
    </Box>
  )
}

// memo化することで音声のOn/Offを切り替えた際などのちらつきを防いでいる
const RemoteVideoPlayer: React.FC<{ track: IRemoteVideoTrack }> = memo(
  function RemoteVideoPlayer({ track }) {
    return (
      <RemoteVideoTrack
        play
        track={track}
        videoPlayerConfig={{ fit: 'contain' }}
      />
    )
  },
)
